import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { MST1011_R01Dto } from './Dto_MST1011_R01';
import { useMST1011_R01Store } from './Store_MST1011_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST1011_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST1011_R01Dto> | null>;
	defaultMST1011_R01Row: MST1011_R01Dto;
}

export const Table_MST1011_R01 = (props: Props) => {
	const { gridRef, defaultMST1011_R01Row } = props;

	const {
		MST1011_R01List,
		selectedMST1011_R01,
		setSelectedMST1011_R01,
		initSelectedMST1011_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useMST1011_R01Store();

	const { brandCdList, itemGroupList, itemAccountList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'itemCd',
		gridData: MST1011_R01List,
		defaultGridData: defaultMST1011_R01Row,
		selectedRow: selectedMST1011_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		modelNo: '모델명',
		brandCd: '브랜드',
		itemGroup: '품목그룹',
		itemAccount: '품목계정',
		stockQty: '재고량',
		remark: '비고',
	};

	const createCodeToNameRenderer = useCallback((
		codeList: { minorCd: string; codeNm: string }[],
		field: keyof MST1011_R01Dto,
	) => (params: { data?: MST1011_R01Dto }) => {
		if (!params.data) return '';
		const codeValue = params.data[field];
		if (codeValue === null || codeValue === undefined) return '';
		const foundItem = codeList.find((item) => item.minorCd === codeValue);
		return foundItem ? foundItem.codeNm : String(codeValue);
	}, []);

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<MST1011_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 95,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 210,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
		{
			headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'locationNm'),
		},
		{
			headerName: '모델명', colId: 'modelNo', field: 'modelNo', width: 160,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'modelNo'),
		},
		{
			headerName: '브랜드', colId: 'brandCd', field: 'brandCd', width: 100,
			headerClass: aggridHeaderClass,
			cellRenderer: createCodeToNameRenderer(brandCdList, 'brandCd'),
			cellStyle: (p) => getCellStyle(p, 'brandCd'),
		},
		{
			headerName: '품목그룹', colId: 'itemGroup', field: 'itemGroup', width: 100,
			headerClass: aggridHeaderClass,
			cellRenderer: createCodeToNameRenderer(itemGroupList, 'itemGroup'),
			cellStyle: (p) => getCellStyle(p, 'itemGroup'),
		},
		{
			headerName: '품목계정', colId: 'itemAccount', field: 'itemAccount', width: 100,
			headerClass: aggridHeaderClass,
			cellRenderer: createCodeToNameRenderer(itemAccountList, 'itemAccount'),
			cellStyle: (p) => getCellStyle(p, 'itemAccount'),
		},
		{
			headerName: '재고량', colId: 'stockQty', field: 'stockQty', width: 80,
			headerClass: aggridHeaderClass,
			cellRenderer: (p: { data?: MST1011_R01Dto }) => p.data?.stockQty?.toLocaleString(),
			cellStyle: (p) => getCellStyle(p, 'stockQty'),
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark',
			headerClass: aggridHeaderClass,
			cellRenderer: (p: { data?: MST1011_R01Dto }) => p.data?.remark,
			cellStyle: (p) => getCellStyle(p, 'remark'),
		},
	], [getCellStyle, createCodeToNameRenderer, brandCdList, itemGroupList, itemAccountList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST1011_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST1011_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST1011_R01Dto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST1011_R01?.itemCd === rowData.itemCd;
		setSelectedMST1011_R01(isSelected ? undefined : rowData);
	}, [selectedMST1011_R01, setSelectedMST1011_R01]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		itemCd: `${MST1011_R01List.length.toLocaleString()}건`,
		stockQty: MST1011_R01List.reduce((sum, item) => sum + item.stockQty, 0),
		editable: false,
	}]), [MST1011_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [MST1011_R01List, selectedMST1011_R01]);

	return (
		<GridContextWrapper<MST1011_R01Dto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			enableCellCalculation
			initSelected={initSelectedMST1011_R01}
			defaultRowData={defaultMST1011_R01Row}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST1011_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST1011_R01Dto>({
					idKey: 'itemCd',
					selectedList: selectedMST1011_R01 ? [selectedMST1011_R01.itemCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
